import { ComponentType, lazy } from 'react';
import { hardReloadBustingStaleCaches } from './chunk-reload-recovery';

/**
 * Wraps React.lazy() with retry logic to handle chunk loading failures
 * after deployments (stale chunk hashes).
 *
 * On failure it retries the import once, and if it still fails it
 * clears the service worker + Cache Storage and does a hard page
 * reload (once) so the browser fetches the new HTML with updated
 * chunk references — see `chunk-reload-recovery.ts` for why a plain
 * reload alone isn't enough.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const RELOAD_KEY = 'chunk-reload';

    try {
      const mod = await importFn();
      // Recovery succeeded — drop the guard so the NEXT deployment that lands
      // in this same tab is allowed its own reload. Leaving the flag set made
      // the second deploy of a session fail straight to the error screen
      // ("sometimes users see it"), because the reload branch was skipped.
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch {
      // First retry: the network may have been temporarily unavailable
      try {
        const mod = await importFn();
        sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      } catch {
        // If we haven't reloaded yet, do a hard reload to get fresh HTML
        // with updated chunk references from the latest deployment
        const hasReloaded = sessionStorage.getItem(RELOAD_KEY);
        if (!hasReloaded) {
          sessionStorage.setItem(RELOAD_KEY, 'true');
          void hardReloadBustingStaleCaches();
          // Return a never-resolving promise to prevent React from rendering
          // while the page reloads
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          return new Promise(() => {});
        }

        // Already reloaded once — clear the flag and let the error propagate
        // so the ErrorBoundary can show a friendly message
        sessionStorage.removeItem(RELOAD_KEY);
        throw new Error(
          'Unable to load page. Please check your internet connection and try again.'
        );
      }
    }
  });
}
